// Tree visualization types for D3.js

import type { BranchStatus, BranchType, MessageRole } from './conversation'

// Node types in the tree
export type TreeNodeType = 
  | 'root'           // Initial situation
  | 'question'       // QAI question
  | 'answer'         // Human answer
  | 'branch_point'   // Where branches split
  | 'termination'    // End of a branch

// Tree node for D3
export interface TreeNode {
  id: string
  type: TreeNodeType
  label: string
  shortLabel?: string // For display in small nodes
  
  // Branch info
  branchId: string
  branchName: string
  branchType: BranchType
  branchStatus: BranchStatus
  
  // Position in tree
  depth: number
  round: number
  orderInRound: number
  
  // Content
  content: string
  role?: MessageRole
  
  // Metadata
  confidenceScore?: number
  isFirstPrinciple?: boolean
  ai3cElement?: string
  
  // Timestamps
  createdAt: number
  
  // D3 computed properties (added during layout)
  x?: number
  y?: number
  parent?: TreeNode
  children?: TreeNode[]
}

// Tree edge for D3
export interface TreeEdge {
  id: string
  source: string  // Node ID
  target: string  // Node ID
  
  // Visual properties
  type: 'sequential' | 'parallel' | 'branch'
  status: BranchStatus
  
  // D3 computed properties
  sourceNode?: TreeNode
  targetNode?: TreeNode
}

// Tree metadata
export interface TreeMetadata {
  totalNodes: number
  totalBranches: number
  maxDepth: number
  branchesByStatus: {
    active: number
    terminated_natural: number
    terminated_manual: number
    pruned: number
  }
  nodesByType: {
    root: number
    question: number
    answer: number
    branch_point: number
    termination: number
  }
}

// Tree data structure
export interface TreeData {
  nodes: TreeNode[]
  edges: TreeEdge[]
  metadata: TreeMetadata
  rootId: string
}

// D3 specific types
export interface D3TreeNode extends TreeNode {
  x: number
  y: number
  // `parent` is optional/undefined to remain compatible with the base
  // `TreeNode.parent?: TreeNode` shape (TS rejects narrowing to `| null`).
  parent?: D3TreeNode
  children: D3TreeNode[]
  data: TreeNode
}

// Tree visualization config
export interface TreeConfig {
  width: number
  height: number
  nodeRadius: number
  levelHeight: number
  siblingSpacing: number
  branchSpacing: number

  // Rectangular node dimensions used by the d3-tree layout. The layout
  // uses `nodeWidth + horizontalSpacing` and `nodeHeight + verticalSpacing`
  // as the per-node footprint.
  nodeWidth: number
  nodeHeight: number
  horizontalSpacing: number
  verticalSpacing: number
  
  // Colors
  colors: {
    root: string
    question: string
    answer: string
    branchPoint: string
    termination: string
    edge: string
    edgeActive: string
    highlight: string
  }
  
  // Interactions
  enableZoom: boolean
  enablePan: boolean
  enableNodeClick: boolean
  enableTooltips: boolean
}

// Default tree config
export const DEFAULT_TREE_CONFIG: TreeConfig = {
  width: 800,
  height: 600,
  nodeRadius: 20,
  levelHeight: 80,
  siblingSpacing: 60,
  branchSpacing: 120,

  nodeWidth: 180,
  nodeHeight: 56,
  horizontalSpacing: 40,
  verticalSpacing: 60,
  
  colors: {
    root: '#97c459',      // Human/green
    question: '#ed93b1',  // QAI/pink
    answer: '#5dcaa5',    // Human answer/teal
    branchPoint: '#afa9ec', // Purple
    termination: '#fcf6bd', // Yellow
    edge: '#cbd5e1',      // Slate-300
    edgeActive: '#a9def9', // Icy blue
    highlight: '#ff99c8', // Baby pink
  },
  
  enableZoom: true,
  enablePan: true,
  enableNodeClick: true,
  enableTooltips: true,
}

// Tree interaction events
export interface TreeInteractionEvent {
  type: 'node_click' | 'node_hover' | 'node_leave' | 'zoom' | 'pan'
  nodeId?: string
  node?: TreeNode
  position?: { x: number; y: number }
  zoom?: number
}

// Tree export formats
export interface TreeExportOptions {
  format: 'svg' | 'png' | 'mermaid' | 'graphviz'
  includeLabels: boolean
  includeColors: boolean
  width?: number
  height?: number
}
